import { useState, useEffect } from 'react';
import {
  ShoppingBag, Check, ChevronDown, Star, Truck,
  RefreshCw, Leaf, Atom, FlaskConical, Zap, Shield, Plus, Minus,
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { PRODUCT_IMAGES, PRODUCT_PRICE, BUNDLE_2_PRICE } from '../constants';
import { trackViewContent } from '../utils/metaPixel';
import SEO from '../components/SEO';
import { useInView } from '../hooks/useInView';

// ─── Data ────────────────────────────────────────────────────────────────────

const SYSTEM_CONCEPTS = [
  { label: 'LIBIDO',       sub: 'Desire & drive'          },
  { label: 'ERECTION',     sub: 'Neurovascular response'  },
  { label: 'ORGASM',       sub: 'Neural response'         },
  { label: 'EJACULATION',  sub: 'Reproductive reflex'     },
];

const INGREDIENTS = [
  {
    Icon: Leaf,
    name: 'Pygeum Africanum',
    dose: '500mg',
    tag: 'Botanical Extract',
    desc: 'Traditionally used to support prostate health and male reproductive function. Contains naturally occurring phytosterols.',
  },
  {
    Icon: Zap,
    name: 'Catuaba Bark',
    dose: '465mg',
    tag: 'Amazonian Herb',
    desc: 'Traditionally used in South American herbal practice to support male vitality and reproductive wellbeing.',
  },
  {
    Icon: FlaskConical,
    name: 'Korean Ginseng',
    dose: '400mg',
    tag: 'Adaptogenic Root',
    desc: 'Well-known adaptogenic root traditionally used to support vitality and general wellbeing. Used in Korean traditional medicine for centuries.',
  },
  {
    Icon: Atom,
    name: 'Zinc',
    dose: '10mg',
    tag: 'Essential Mineral',
    desc: 'Zinc contributes to normal fertility and reproduction and to the maintenance of normal testosterone levels in the blood.*',
    authorized: true,
  },
  {
    Icon: Shield,
    name: 'Selenium',
    dose: '55mcg',
    tag: 'Trace Mineral',
    desc: 'Selenium contributes to normal spermatogenesis.* Works synergistically with Zinc to support male reproductive wellbeing.',
    authorized: true,
  },
];

const LP_FAQS = [
  {
    q: 'What is RELOAD?',
    a: "RELOAD is a daily men's nutritional supplement formulated to support male reproductive wellbeing. It contains five botanical and mineral ingredients and is designed to be taken as part of your daily routine.",
  },
  {
    q: 'Is RELOAD an erection pill?',
    a: "No. RELOAD is not an erectile dysfunction treatment or a pharmaceutical drug. It is a food supplement designed to support general male reproductive wellbeing as part of a consistent daily routine.",
  },
  {
    q: 'How do I take RELOAD?',
    a: 'Take 2 capsules daily, ideally with your morning meal and a full glass of water. Do not exceed the recommended daily dose.',
  },
  {
    q: 'How long should I take RELOAD for?',
    a: 'RELOAD is designed for daily use as part of an ongoing wellness routine. For best results, take consistently as directed on the label.',
  },
  {
    q: "What's in RELOAD?",
    a: 'RELOAD contains five carefully selected ingredients: Pygeum Africanum (500mg), Catuaba Bark (465mg), Korean Ginseng (400mg), Zinc (10mg), and Selenium (55mcg). No synthetic hormones, no artificial additives.',
  },
  {
    q: 'Can I take RELOAD every day?',
    a: 'Yes. RELOAD is formulated for daily use. If you are taking prescription medication or have an existing health condition, please consult your GP before beginning any new supplement.',
  },
];

// ─── Shared style tokens ─────────────────────────────────────────────────────
const ff = { outfit: 'Outfit, sans-serif', manrope: 'Manrope, sans-serif' };
const C = {
  dark: '#08111F',
  navy: '#0D1A2E',
  blue: '#1B3D7E',
  offwhite: '#F7F7F5',
  border: '#E2E8F0',
  muted: '#64748B',
  dimWhite: 'rgba(255,255,255,0.55)',
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function MinimalHeader({ totalQty, openCart, scrolled }) {
  return (
    <header
      data-testid="lp-header"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
        padding: '14px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        backgroundColor: scrolled ? 'rgba(8,17,31,0.92)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        transition: 'background-color 0.35s ease, backdrop-filter 0.35s ease',
        borderBottom: scrolled ? '1px solid rgba(27,61,126,0.25)' : 'none',
      }}
    >
      <span style={{ fontFamily: ff.outfit, fontWeight: 800, fontSize: '1.25rem', letterSpacing: '0.25em', color: '#FFFFFF', textTransform: 'uppercase' }}>
        RE<span style={{ color: '#5B8DEF' }}>LOAD</span>
      </span>
      <button
        data-testid="lp-cart-btn"
        onClick={() => openCart(true)}
        style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', color: '#FFFFFF', padding: '6px', display: 'flex', alignItems: 'center' }}
      >
        <ShoppingBag size={20} strokeWidth={1.5} />
        {totalQty > 0 && (
          <span style={{
            position: 'absolute', top: 0, right: 0,
            backgroundColor: C.blue, color: '#FFF',
            fontSize: '0.6rem', fontFamily: ff.manrope, fontWeight: 700,
            borderRadius: '100px', minWidth: '16px', height: '16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
          }}>
            {totalQty}
          </span>
        )}
      </button>
    </header>
  );
}

function CTAButton({ onClick, label, dark = false, testId }) {
  return (
    <button
      data-testid={testId || 'lp-cta'}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        padding: '16px 40px',
        backgroundColor: dark ? '#FFFFFF' : C.blue,
        color: dark ? C.navy : '#FFFFFF',
        border: 'none', borderRadius: '100px',
        fontFamily: ff.manrope, fontSize: '0.82rem', fontWeight: 700,
        letterSpacing: '0.14em', textTransform: 'uppercase',
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
        boxShadow: dark ? 'none' : '0 4px 24px rgba(27,61,126,0.45)',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = dark ? '0 4px 16px rgba(0,0,0,0.15)' : '0 8px 32px rgba(27,61,126,0.55)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = dark ? 'none' : '0 4px 24px rgba(27,61,126,0.45)'; }}
    >
      <ShoppingBag size={15} strokeWidth={2} />
      {label}
    </button>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SexReloaded() {
  const { addToCart, setOpen: openCart, totalQty } = useCart();
  const [selected, setSelected] = useState('one');
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const [s2Ref, s2In] = useInView(0.1);
  const [s3Ref, s3In] = useInView(0.1);
  const [s4Ref, s4In] = useInView(0.05);
  const [s5Ref, s5In] = useInView(0.1);
  const [s6Ref, s6In] = useInView(0.1);
  const [s7Ref, s7In] = useInView(0.1);
  const [s8Ref, s8In] = useInView(0.1);

  useEffect(() => {
    trackViewContent(PRODUCT_PRICE);
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddToCart = () => {
    const qty = selected === 'two' ? 2 : 1;
    addToCart(qty, PRODUCT_IMAGES.twoBottles);
  };

  const price = selected === 'two' ? BUNDLE_2_PRICE : PRODUCT_PRICE;

  return (
    <>
      <SEO
        title="Sex. Reloaded."
        description="RELOAD is a daily men's supplement combining botanical and mineral ingredients, including zinc and selenium, to support male reproductive wellbeing. £24.99."
        canonical="/pages/sex-reloaded"
      />

      <MinimalHeader totalQty={totalQty} openCart={openCart} scrolled={scrolled} />

      {/* ── SECTION 1: HERO ──────────────────────────────────────────────── */}
      <section
        data-testid="lp-hero"
        style={{
          backgroundColor: C.dark,
          minHeight: '100vh',
          display: 'flex', alignItems: 'center',
          position: 'relative', overflow: 'hidden',
          paddingTop: '80px',
        }}
      >
        {/* Atmospheric glow behind bottle */}
        <div style={{
          position: 'absolute', right: '5%', top: '50%', transform: 'translateY(-50%)',
          width: 'min(480px, 90vw)', height: 'min(480px, 90vw)',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(27,61,126,0.18) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-0">

            {/* Left: Copy */}
            <div style={{ flex: '0 0 auto', width: '100%', maxWidth: '560px', zIndex: 2 }}>
              <span style={{
                display: 'inline-block', marginBottom: '20px',
                fontFamily: ff.manrope, fontSize: '0.68rem', fontWeight: 700,
                letterSpacing: '0.28em', textTransform: 'uppercase',
                color: '#5B8DEF',
                borderBottom: '1px solid rgba(91,141,239,0.3)', paddingBottom: '6px',
              }}>
                RELOAD Nutrition
              </span>

              <h1 style={{
                fontFamily: ff.outfit, fontWeight: 800,
                fontSize: 'clamp(3.2rem, 9vw, 6.5rem)',
                color: '#FFFFFF', letterSpacing: '-0.03em', lineHeight: 0.95,
                marginBottom: '28px',
              }}>
                SEX.<br />
                <span style={{ color: '#5B8DEF' }}>RELOADED.</span>
              </h1>

              <p style={{
                fontFamily: ff.outfit, fontWeight: 600,
                fontSize: 'clamp(1.05rem, 2.5vw, 1.35rem)',
                color: '#FFFFFF', letterSpacing: '-0.01em', lineHeight: 1.3,
                marginBottom: '16px',
              }}>
                Sexual wellbeing is more than libido.
              </p>

              <p style={{
                fontFamily: ff.manrope, fontSize: '0.95rem', fontWeight: 400,
                color: C.dimWhite, lineHeight: 1.75, marginBottom: '20px',
                maxWidth: '460px',
              }}>
                RELOAD is a daily men&apos;s supplement combining botanical and mineral ingredients,
                including zinc and selenium, to support male reproductive wellbeing.
              </p>

              {/* Authorised claims — prominent, not footnote */}
              <div style={{ marginBottom: '32px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  'Zinc contributes to normal fertility and reproduction.',
                  'Selenium contributes to normal spermatogenesis.',
                ].map(claim => (
                  <div key={claim} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#5B8DEF', marginTop: '7px', flexShrink: 0 }} />
                    <span style={{ fontFamily: ff.manrope, fontSize: '0.82rem', fontWeight: 500, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{claim}</span>
                  </div>
                ))}
              </div>

              <CTAButton onClick={handleAddToCart} label="Try RELOAD" testId="lp-hero-cta" />

              <p style={{
                marginTop: '16px',
                fontFamily: ff.manrope, fontSize: '0.75rem', color: 'rgba(255,255,255,0.38)',
                letterSpacing: '0.04em',
              }}>
                Daily men's wellness. No awkward conversations required.
              </p>
            </div>

            {/* Right: Bottle */}
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1, paddingLeft: '0' }} className="lg:justify-end lg:pl-16">
              <div style={{ position: 'relative', maxWidth: '320px', width: '100%' }}>
                <img
                  src={PRODUCT_IMAGES.singleBottle}
                  alt="RELOAD Male Performance Formula"
                  data-testid="lp-hero-bottle"
                  className="animate-float"
                  style={{
                    width: '100%', objectFit: 'contain',
                    filter: 'drop-shadow(0 24px 60px rgba(27,61,126,0.5))',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Scroll cue */}
          <div style={{ textAlign: 'center', paddingTop: '40px', paddingBottom: '24px' }}>
            <ChevronDown
              size={20}
              className="animate-bounce"
              style={{ color: 'rgba(255,255,255,0.25)' }}
            />
          </div>
        </div>
      </section>

      {/* ── SECTION 2: SEX ISN'T ONE THING ──────────────────────────────── */}
      <section
        data-testid="lp-system"
        style={{ backgroundColor: '#FFFFFF', padding: '96px 0' }}
      >
        <div ref={s2Ref} className="max-w-5xl mx-auto px-6 md:px-12">

          <div className={`fade-up ${s2In ? 'visible' : ''}`} style={{ marginBottom: '48px' }}>
            <span style={{ fontFamily: ff.manrope, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: C.blue, display: 'block', marginBottom: '16px' }}>
              Male Sexual Health
            </span>
            <h2 style={{
              fontFamily: ff.outfit, fontWeight: 800,
              fontSize: 'clamp(2rem, 5vw, 3.6rem)',
              color: C.navy, letterSpacing: '-0.03em', lineHeight: 1.0,
              marginBottom: '20px',
            }}>
              SEX ISN&apos;T<br />ONE THING.
            </h2>
            <p style={{ fontFamily: ff.manrope, fontSize: '1rem', color: C.muted, lineHeight: 1.75, maxWidth: '540px' }}>
              These processes are connected, but each is controlled by a different biological system.
            </p>
          </div>

          {/* Mobile: 2×2 grid */}
          <div className={`grid grid-cols-2 md:hidden gap-3 fade-up delay-1 ${s2In ? 'visible' : ''}`}>
            {SYSTEM_CONCEPTS.map(({ label, sub }) => (
              <div key={label} style={{
                padding: '20px 16px', border: `1.5px solid ${C.border}`,
                borderRadius: '14px', textAlign: 'center',
              }}>
                <p style={{ fontFamily: ff.outfit, fontWeight: 800, fontSize: '0.95rem', color: C.navy, letterSpacing: '0.1em', marginBottom: '4px' }}>{label}</p>
                <p style={{ fontFamily: ff.manrope, fontSize: '0.68rem', color: C.muted }}>{sub}</p>
              </div>
            ))}
          </div>

          {/* Desktop: row with ≠ separators */}
          <div className={`hidden md:flex items-stretch gap-0 fade-up delay-1 ${s2In ? 'visible' : ''}`}>
            {SYSTEM_CONCEPTS.map(({ label, sub }, i) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <div style={{
                  flex: 1, padding: '28px 20px', border: `1.5px solid ${C.border}`,
                  borderRadius: '14px', textAlign: 'center',
                  borderRight: i < 3 ? 'none' : undefined,
                  borderTopRightRadius: i < 3 ? 0 : 14,
                  borderBottomRightRadius: i < 3 ? 0 : 14,
                  borderTopLeftRadius: i > 0 ? 0 : 14,
                  borderBottomLeftRadius: i > 0 ? 0 : 14,
                }}>
                  <p style={{ fontFamily: ff.outfit, fontWeight: 800, fontSize: '1rem', color: C.navy, letterSpacing: '0.1em', marginBottom: '6px' }}>{label}</p>
                  <p style={{ fontFamily: ff.manrope, fontSize: '0.72rem', color: C.muted }}>{sub}</p>
                </div>
                {i < 3 && (
                  <div style={{ padding: '0 6px', fontFamily: ff.outfit, fontWeight: 800, fontSize: '1.1rem', color: C.blue, flexShrink: 0 }}>≠</div>
                )}
              </div>
            ))}
          </div>

          <p className={`fade-up delay-2 ${s2In ? 'visible' : ''}`} style={{
            marginTop: '36px', fontFamily: ff.manrope, fontSize: '0.95rem',
            color: C.muted, lineHeight: 1.8, maxWidth: '620px',
          }}>
            These are different biological processes. RELOAD doesn&apos;t claim to alter each one.
            Its nutritional role is focused on male reproductive wellbeing, including zinc for normal
            fertility and reproduction and selenium for normal spermatogenesis.
          </p>
        </div>
      </section>

      {/* ── SECTION 3: WHAT IS RELOAD ────────────────────────────────────── */}
      <section
        data-testid="lp-what"
        style={{ backgroundColor: C.offwhite, padding: '96px 0' }}
      >
        <div ref={s3Ref} className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

            {/* Bottle image */}
            <div className={`flex-shrink-0 fade-up delay-1 ${s3In ? 'visible' : ''}`} style={{ width: '100%', maxWidth: '320px' }}>
              <div style={{
                backgroundColor: '#FFFFFF', borderRadius: '24px',
                padding: '40px 32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 40px rgba(13,26,46,0.08)', border: `1px solid ${C.border}`,
                position: 'relative',
              }}>
                <div style={{
                  position: 'absolute', inset: '-10px', borderRadius: '30px',
                  background: 'radial-gradient(circle, rgba(27,61,126,0.05) 0%, transparent 70%)',
                  pointerEvents: 'none',
                }} />
                <img
                  src={PRODUCT_IMAGES.twoBottles}
                  alt="RELOAD — Male Performance Formula"
                  style={{ width: '100%', objectFit: 'contain', maxHeight: '300px' }}
                />
              </div>
            </div>

            {/* Copy */}
            <div className={`fade-up ${s3In ? 'visible' : ''}`} style={{ flex: 1 }}>
              <span style={{ fontFamily: ff.manrope, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: C.blue, display: 'block', marginBottom: '16px' }}>
                The Supplement
              </span>
              <h2 style={{
                fontFamily: ff.outfit, fontWeight: 800,
                fontSize: 'clamp(1.8rem, 4vw, 3rem)',
                color: C.navy, letterSpacing: '-0.025em', lineHeight: 1.05,
                marginBottom: '28px',
              }}>
                WHAT IS RELOAD?
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '36px' }}>
                {[
                  "RELOAD is a daily men's supplement combining five botanical and mineral ingredients in an easy-to-take daily serving.",
                  "It is formulated to support male reproductive wellbeing as part of a consistent daily routine.",
                  "RELOAD is a food supplement, not a medicine or pharmaceutical treatment.",
                  "Two capsules a day. A simple addition to your daily routine.",
                ].map((text, i) => (
                  <p key={i} style={{ fontFamily: ff.manrope, fontSize: '0.95rem', color: C.muted, lineHeight: 1.8 }}>{text}</p>
                ))}
              </div>

              <CTAButton onClick={handleAddToCart} label="Shop RELOAD" testId="lp-what-cta" />
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 4: THE FORMULA ───────────────────────────────────────── */}
      <section
        data-testid="lp-formula"
        style={{ backgroundColor: C.navy, padding: '96px 0' }}
      >
        <div ref={s4Ref} className="max-w-5xl mx-auto px-6 md:px-12">

          <div className={`text-center mb-14 fade-up ${s4In ? 'visible' : ''}`}>
            <span style={{ fontFamily: ff.manrope, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#5B8DEF', display: 'block', marginBottom: '14px' }}>
              What&apos;s Inside
            </span>
            <h2 style={{
              fontFamily: ff.outfit, fontWeight: 800,
              fontSize: 'clamp(1.8rem, 4vw, 3rem)',
              color: '#FFFFFF', letterSpacing: '-0.025em', lineHeight: 1.05,
            }}>
              THE FORMULA
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {INGREDIENTS.map(({ Icon, name, dose, tag, desc, authorized }, i) => (
              <div
                key={name}
                data-testid={`lp-ingredient-${i}`}
                className={`fade-up delay-${Math.min(i + 1, 4)} ${s4In ? 'visible' : ''}`}
                style={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '14px', overflow: 'hidden',
                  display: 'flex', alignItems: 'stretch',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
              >
                {/* Blue accent */}
                <div style={{ width: '3px', backgroundColor: '#5B8DEF', flexShrink: 0 }} />

                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '16px', padding: '20px 20px' }}>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '10px', flexShrink: 0,
                    backgroundColor: 'rgba(91,141,239,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={18} style={{ color: '#5B8DEF' }} strokeWidth={1.5} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontFamily: ff.outfit, fontWeight: 700, fontSize: '0.98rem', color: '#FFFFFF' }}>{name}</span>
                      <span style={{
                        fontFamily: ff.manrope, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                        color: '#94A3B8', backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)',
                        padding: '2px 8px', borderRadius: '100px',
                      }}>{tag}</span>
                      {authorized && (
                        <span style={{
                          fontFamily: ff.manrope, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em',
                          textTransform: 'uppercase', color: '#5B8DEF', backgroundColor: 'rgba(91,141,239,0.12)',
                          border: '1px solid rgba(91,141,239,0.25)', padding: '2px 8px', borderRadius: '100px',
                        }}>Authorised Claim</span>
                      )}
                    </div>
                    <p style={{ fontFamily: ff.manrope, fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.65 }}>{desc}</p>
                  </div>
                </div>

                {/* Dosage */}
                <div style={{
                  flexShrink: 0, padding: '0 20px',
                  borderLeft: '1px solid rgba(255,255,255,0.08)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: '90px',
                }}>
                  <p style={{ fontFamily: ff.outfit, fontWeight: 800, fontSize: '1.3rem', color: '#5B8DEF', lineHeight: 1, marginBottom: '3px' }}>{dose}</p>
                  <p style={{ fontFamily: ff.manrope, fontSize: '0.6rem', fontWeight: 600, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Per Serving</p>
                </div>
              </div>
            ))}
          </div>

          <p className={`fade-up delay-4 ${s4In ? 'visible' : ''}`} style={{
            marginTop: '24px', textAlign: 'center',
            fontFamily: ff.manrope, fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.6,
          }}>
            * Authorised EU/UK health claim. Food supplement. Not intended to diagnose, treat, cure, or prevent any disease.
          </p>
        </div>
      </section>

      {/* ── SECTION 5: NOT A MAGIC PILL ──────────────────────────────────── */}
      <section
        data-testid="lp-not-magic"
        style={{ backgroundColor: '#FFFFFF', padding: '96px 0' }}
      >
        <div ref={s5Ref} className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-24">

            <div className={`fade-up flex-1 ${s5In ? 'visible' : ''}`} style={{ flexBasis: '50%' }}>
              <h2 style={{
                fontFamily: ff.outfit, fontWeight: 800,
                fontSize: 'clamp(1.9rem, 4.5vw, 3.2rem)',
                color: C.navy, letterSpacing: '-0.03em', lineHeight: 1.0,
              }}>
                NOT A<br />MAGIC PILL.<br />
                <span style={{ color: C.blue }}>A BETTER<br />DAILY ROUTINE.</span>
              </h2>
            </div>

            <div className={`flex-1 fade-up delay-1 ${s5In ? 'visible' : ''}`}>
              <p style={{ fontFamily: ff.manrope, fontSize: '1.0rem', color: C.muted, lineHeight: 1.85, marginBottom: '18px' }}>
                RELOAD isn&apos;t an instant erection pill or pharmaceutical treatment.
              </p>
              <p style={{ fontFamily: ff.manrope, fontSize: '1.0rem', color: C.muted, lineHeight: 1.85, marginBottom: '18px' }}>
                It is a daily food supplement formulated for men who want to support their reproductive
                wellbeing as part of their overall health.
              </p>
              <p style={{ fontFamily: ff.manrope, fontSize: '1.0rem', color: C.muted, lineHeight: 1.85 }}>
                Built for consistency, not quick fixes.
              </p>

              <div style={{ marginTop: '32px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  'No synthetic hormones',
                  'No pharmaceutical ingredients',
                ].map(item => (
                  <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'rgba(27,61,126,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Check size={11} style={{ color: C.blue }} strokeWidth={2.5} />
                    </div>
                    <span style={{ fontFamily: ff.manrope, fontSize: '0.88rem', color: C.navy, fontWeight: 500 }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 6: PURCHASE ──────────────────────────────────────────── */}
      <section
        data-testid="lp-purchase"
        style={{ backgroundColor: C.offwhite, padding: '96px 0' }}
      >
        <div ref={s6Ref} className="max-w-6xl mx-auto px-6 md:px-12">

          <div className={`text-center mb-14 fade-up ${s6In ? 'visible' : ''}`}>
            <span style={{ fontFamily: ff.manrope, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: C.blue, display: 'block', marginBottom: '14px' }}>
              Start Today
            </span>
            <h2 style={{
              fontFamily: ff.outfit, fontWeight: 800,
              fontSize: 'clamp(1.8rem, 4vw, 3rem)',
              color: C.navy, letterSpacing: '-0.025em',
            }}>
              RELOAD YOUR ROUTINE
            </h2>
          </div>

          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">

            {/* Bottle */}
            <div className={`flex-shrink-0 fade-up delay-1 ${s6In ? 'visible' : ''}`} style={{ width: '100%', maxWidth: '280px' }}>
              <div style={{
                backgroundColor: '#FFFFFF', borderRadius: '20px', padding: '36px 28px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 40px rgba(13,26,46,0.08)', border: `1px solid ${C.border}`,
              }}>
                <img
                  src={PRODUCT_IMAGES.twoBottles}
                  alt="RELOAD"
                  style={{ width: '100%', objectFit: 'contain', maxHeight: '260px' }}
                />
              </div>
              {/* Verified Trustpilot rating */}
              <a
                href="https://www.trustpilot.com/review/reload-nutrition.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginTop: '16px', textDecoration: 'none' }}
              >
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={13} fill={i < 4 ? C.blue : 'none'} stroke={C.blue} />
                ))}
                <span style={{ fontFamily: ff.manrope, fontSize: '0.78rem', color: C.muted, fontWeight: 600 }}>
                  4.2 / 5 on Trustpilot · 6 reviews
                </span>
              </a>
            </div>

            {/* Purchase panel */}
            <div className={`flex-1 fade-up ${s6In ? 'visible' : ''}`}>
              {/* Options */}
              <p style={{ fontFamily: ff.manrope, fontSize: '0.68rem', fontWeight: 700, color: C.muted, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '12px' }}>
                Choose your supply
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
                {[
                  { id: 'one',  label: '1 Bottle',  price: `£${PRODUCT_PRICE.toFixed(2)}`,  sub: 'Delivery calculated at checkout', badge: null },
                  { id: 'two',  label: '2 Bottles', price: `£${BUNDLE_2_PRICE.toFixed(2)}`, sub: 'Free UK delivery included',       badge: 'FREE UK DELIVERY' },
                ].map(opt => {
                  const isActive = selected === opt.id;
                  return (
                    <button
                      key={opt.id}
                      data-testid={`lp-option-${opt.id}`}
                      onClick={() => setSelected(opt.id)}
                      style={{
                        width: '100%', textAlign: 'left', cursor: 'pointer',
                        padding: '16px 20px',
                        border: `2px solid ${isActive ? C.blue : C.border}`,
                        borderRadius: '14px',
                        backgroundColor: isActive ? 'rgba(27,61,126,0.04)' : '#FFFFFF',
                        display: 'flex', alignItems: 'center', gap: '14px',
                        transition: 'border-color 0.2s, background-color 0.2s',
                        position: 'relative',
                      }}
                    >
                      {opt.badge && (
                        <span style={{
                          position: 'absolute', top: '-9px', right: '12px',
                          backgroundColor: C.blue, color: '#FFF',
                          fontFamily: ff.manrope, fontSize: '0.6rem', fontWeight: 700,
                          letterSpacing: '0.1em', textTransform: 'uppercase',
                          padding: '2px 10px', borderRadius: '100px',
                        }}>
                          {opt.badge}
                        </span>
                      )}
                      <div style={{
                        width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                        border: `2px solid ${isActive ? C.blue : '#CBD5E1'}`,
                        backgroundColor: isActive ? C.blue : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s',
                      }}>
                        {isActive && <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#FFF' }} />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontFamily: ff.outfit, fontWeight: 700, fontSize: '0.9rem', color: isActive ? C.blue : C.navy, display: 'block', marginBottom: '2px' }}>{opt.label}</span>
                        <span style={{ fontFamily: ff.manrope, fontSize: '0.72rem', color: C.muted }}>{opt.sub}</span>
                      </div>
                      <span style={{ fontFamily: ff.outfit, fontWeight: 800, fontSize: '1.05rem', color: isActive ? C.blue : C.navy }}>{opt.price}</span>
                    </button>
                  );
                })}
              </div>

              {/* Main CTA */}
              <button
                data-testid="lp-purchase-cta"
                onClick={handleAddToCart}
                style={{
                  width: '100%', padding: '17px',
                  backgroundColor: C.blue, color: '#FFF', border: 'none',
                  borderRadius: '14px', cursor: 'pointer',
                  fontFamily: ff.manrope, fontSize: '0.9rem', fontWeight: 700,
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                  transition: 'background-color 0.2s, transform 0.15s',
                  marginBottom: '12px',
                  boxShadow: '0 4px 20px rgba(27,61,126,0.35)',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#152e61'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = C.blue; e.currentTarget.style.transform = 'none'; }}
              >
                <ShoppingBag size={17} strokeWidth={2} />
                Reload Your Routine — £{price.toFixed(2)}
              </button>

              <button
                onClick={() => openCart(true)}
                style={{
                  width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: ff.manrope, fontSize: '0.78rem', color: C.muted, padding: '4px',
                }}
              >
                View basket
              </button>

              {/* Trust badges */}
              <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '8px', padding: '16px', backgroundColor: '#FFFFFF', borderRadius: '12px', border: `1px solid ${C.border}` }}>
                {[
                  { Icon: RefreshCw, label: '30-Day Money-Back Guarantee' },
                  { Icon: Truck,     label: 'Free UK Delivery on 2+ Bottles' },
                  { Icon: Leaf,      label: 'Natural Botanical Formula' },
                ].map(({ Icon, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon size={13} style={{ color: C.blue, flexShrink: 0 }} strokeWidth={1.5} />
                    <span style={{ fontFamily: ff.manrope, fontSize: '0.78rem', color: C.muted, fontWeight: 600 }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 7: FAQ ───────────────────────────────────────────────── */}
      <section
        data-testid="lp-faq"
        style={{ backgroundColor: '#FFFFFF', padding: '96px 0' }}
      >
        <div ref={s7Ref} className="max-w-3xl mx-auto px-6 md:px-12">
          <div className={`text-center mb-14 fade-up ${s7In ? 'visible' : ''}`}>
            <span style={{ fontFamily: ff.manrope, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: C.blue, display: 'block', marginBottom: '14px' }}>
              Common Questions
            </span>
            <h2 style={{ fontFamily: ff.outfit, fontWeight: 800, fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', color: C.navy, letterSpacing: '-0.025em' }}>
              GOT QUESTIONS?
            </h2>
          </div>

          <div className={`fade-up delay-1 ${s7In ? 'visible' : ''}`} style={{ backgroundColor: C.offwhite, borderRadius: '16px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
            {LP_FAQS.map((faq, i) => (
              <div key={faq.q} style={{ borderBottom: i < LP_FAQS.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                <button
                  data-testid={`lp-faq-${i}`}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '20px 24px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '16px',
                  }}
                >
                  <span style={{ fontFamily: ff.outfit, fontSize: '0.95rem', fontWeight: 600, color: openFaq === i ? C.blue : C.navy, lineHeight: 1.4, transition: 'color 0.2s' }}>
                    {faq.q}
                  </span>
                  <div style={{
                    width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0,
                    border: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: openFaq === i ? 'rgba(27,61,126,0.08)' : '#FFF',
                    transition: 'all 0.2s',
                  }}>
                    {openFaq === i
                      ? <Minus size={12} style={{ color: C.blue }} />
                      : <Plus  size={12} style={{ color: C.muted }} />}
                  </div>
                </button>
                {openFaq === i && (
                  <div style={{ padding: '0 24px 20px' }}>
                    <p style={{ fontFamily: ff.manrope, fontSize: '0.88rem', color: C.muted, lineHeight: 1.85 }}>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 8: FINAL CTA ─────────────────────────────────────────── */}
      <section
        data-testid="lp-final-cta"
        style={{ backgroundColor: C.dark, padding: '96px 0' }}
      >
        <div ref={s8Ref} className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

            <div className={`fade-up order-2 lg:order-1 flex-shrink-0 ${s8In ? 'visible' : ''}`} style={{ width: '100%', maxWidth: '260px' }}>
              <img
                src={PRODUCT_IMAGES.floatingBottles}
                alt="RELOAD"
                className="animate-float"
                style={{ width: '100%', objectFit: 'contain', filter: 'drop-shadow(0 20px 50px rgba(27,61,126,0.5))' }}
              />
            </div>

            <div className={`flex-1 text-center lg:text-left fade-up delay-1 order-1 lg:order-2 ${s8In ? 'visible' : ''}`}>
              <h2 style={{
                fontFamily: ff.outfit, fontWeight: 800,
                fontSize: 'clamp(1.8rem, 4.5vw, 3.2rem)',
                color: '#FFFFFF', letterSpacing: '-0.03em', lineHeight: 1.05,
                marginBottom: '18px',
              }}>
                YOUR SEX LIFE IS<br />
                PART OF YOUR<br />
                <span style={{ color: '#5B8DEF' }}>HEALTH.</span>
              </h2>

              <p style={{
                fontFamily: ff.outfit, fontWeight: 500,
                fontSize: 'clamp(1rem, 2.5vw, 1.3rem)',
                color: C.dimWhite, marginBottom: '36px', letterSpacing: '-0.01em',
              }}>
                Treat it that way.
              </p>

              <CTAButton onClick={handleAddToCart} label="Try RELOAD" dark testId="lp-final-btn" />

              <p style={{ marginTop: '16px', fontFamily: ff.manrope, fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)' }}>
                £{PRODUCT_PRICE.toFixed(2)} · Free UK delivery on 2+ bottles · 30-day guarantee
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Compliance footer ─────────────────────────────────────────────── */}
      <div style={{ backgroundColor: C.dark, borderTop: '1px solid rgba(255,255,255,0.1)', padding: '32px 24px' }}>
        <p style={{
          fontFamily: ff.manrope, fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)',
          textAlign: 'center', lineHeight: 1.9, maxWidth: '820px', margin: '0 auto',
        }}>
          RELOAD is a food supplement. It is not a medicine and is not intended to diagnose, treat, cure, or prevent any disease or medical condition.
          RELOAD does not treat erectile dysfunction, premature ejaculation, or any other sexual dysfunction. Always read the label and do not exceed the
          recommended daily dose. If you are taking prescription medication or have an existing medical condition, consult your GP before use.
          <br />
          * Zinc contributes to normal fertility and reproduction and to the maintenance of normal testosterone levels in the blood.
          * Selenium contributes to normal spermatogenesis. These are authorised EU/UK health claims.
          <br /><br />
          <span style={{ color: 'rgba(255,255,255,0.35)' }}>
            &copy; {new Date().getFullYear()} RELOAD Nutrition. All rights reserved.&nbsp;|&nbsp;
            <a href="/privacy" style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Privacy Policy</a>&nbsp;|&nbsp;
            <a href="/terms" style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Terms</a>
          </span>
        </p>
      </div>
    </>
  );
}
